import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthProvider, useAuth } from './context/AuthContext';

import Sidebar from './components/Sidebar';
import Header from './components/Header';
import TopNav from './components/TopNav';
import AuthPage from './pages/Auth/AuthPage';
import ChatBot from './components/ChatBot/ChatBot';
import TrialBanner from './components/TrialBanner/TrialBanner';
import { ToastProvider } from './components/Toast';   // Part 5

import Dashboard from './pages/Dashboard/Dashboard';
import Pump from './pages/Pump/Pump';
import Master from './pages/Master/Master';
import Entry from './pages/Entry/Entry';
import Sale from './pages/Sale/Sale';
import MeterReading from './pages/MeterReading/MeterReading';
import LubeSales from './pages/LubeSales/LubeSales';
import CreditSale from './pages/CreditSale/CreditSale';
import SaleBillLubeGST from './pages/SaleBillLubeGST/SaleBillLubeGST';
import DailyReceipt from './pages/DailyReceipt/DailyReceipt';
import ItemLoss from './pages/ItemLoss/ItemLoss';
import BillReceipt from './pages/BillReceipt/BillReceipt';
import Purchase from './pages/Purchase/Purchase';
import Dip from './pages/Dip/Dip';
import D2DSales from './pages/D2DSales/D2DSales';
import Support from './pages/Support/Support';
import Accounting from './pages/Accounting/Accounting';     // Part 3
import FuelBalance from './pages/FuelBalance/FuelBalance';   // Part 4
import AuditLog from './pages/AuditLog/AuditLog';         // Part 6
import Banking from './pages/Banking/Banking';           // Part 8
import Expenses from './pages/Expenses/Expenses';
import Payments from './pages/Payments/Payments';
import RoleManagement from './pages/RoleManagement/RoleManagement';
import UserManagement from './pages/UserManagement/UserManagement';
// import ProductRates from './pages/ProductRates/ProductRates';
import Contra from './pages/Contra/Contra';
import Journal from './pages/Journal/Journal';
import Financials from './pages/Financials/Financials';
import StockLedger from './pages/StockLedger/StockLedger';
import TankTransfer from './pages/TankTransfer/TankTransfer';
import StockAdjustment from './pages/StockAdjustment/StockAdjustment';
import Employees from './pages/Employees/Employees';
import Overtime from './pages/Overtime/Overtime';
import Reconciliation from './pages/Reconciliation/Reconciliation';
import Ledgers from './pages/Ledgers/Ledgers';
import LedgerView from './pages/LedgerView/LedgerView';
import RegisterStation from './pages/RegisterStation/RegisterStation';
import Approvals from './pages/Approvals/Approvals';
import Sitemap from './pages/Sitemap/Sitemap';   // Documentation-only (additive)
import DailyEntry from './pages/DailyEntry/DailyEntry';   // Guided daily workflow (additive)

import './styles/App.css';

const TAB_TO_PAGE = {
  'Meter Reading': { page: 'MeterReading', subPage: null },
  'Lube Sales': { page: 'LubeSales', subPage: null },
  'Sale Bill': { page: 'Sale', subPage: 'SaleList' },
  'Sale Bill [Lube-GST]': { page: 'SaleBillLubeGST', subPage: null },
  'D2D Sales': { page: 'D2DSales', subPage: null },
  'Daily Receipt': { page: 'DailyReceipt', subPage: null },
  'Item Loss': { page: 'ItemLoss', subPage: null },
  'Bill Receipt': { page: 'BillReceipt', subPage: null },
};

function PageContent({ page }) {
  const { t } = useTranslation();
  switch (page) {
    case 'Dashboard': return <Dashboard />;
    case 'Pump':
    case 'Pumps':
    case 'PumpList': return <Master subPage="Pumps" />;
    case 'Nozzle':
    case 'Nozzles':
    case 'NozzleConfig': return <Master subPage="Nozzles" />;
    case 'Master':
    case 'Product':
    case 'Products': return <Master subPage="Products" />;
    case 'Tank':
    case 'Tanks': return <Master subPage="Tanks" />;
    case 'Supplier':
    case 'Suppliers': return <Master subPage="Suppliers" />;
    case 'Customer':
    case 'Customers': return <Master subPage="Customers" />;
    // case 'Entry':         return <Entry subPage="Opening" />;
    // case 'OpeningStock':  return <Entry subPage="Opening" />;
    // case 'ClosingStock':  return <Entry subPage="Closing" />;
    case 'Sale':
    case 'SaleList':
    case 'SaleReturn':
    case 'MeterReading':
    case 'LubeSales':
    case 'CreditSale':
    case 'DailyReceipt':
    case 'BillReceipt': return <Sale subPage={page === 'Sale' ? 'SaleList' : page} />;
    case 'SaleBillLubeGST': return <SaleBillLubeGST />;
    case 'ItemLoss': return <ItemLoss />;
    // case 'D2DSales': return <D2DSales />;
    case 'Purchase':
    case 'PurchaseOrder': return <Purchase subPage="PurchaseOrder" />;
    case 'PurchaseEntry': return <Purchase subPage="PurchaseEntry" />;
    case 'PurchaseReturn': return <Purchase subPage="PurchaseReturn" />;
    case 'Expenses': return <Purchase subPage="Expenses" />;
    case 'Dip':
    case 'DipReading': return <Dip subPage="DipReading" />;
    case 'DipChart': return <Dip subPage="DipChart" />;
    case 'Support': return <Support />;
    case 'Accounting': return <Accounting subPage="Day Book" />;                  // Part 3
    case 'FuelBalance': return <Dip subPage="FuelBalance" />;
    case 'AuditLog': return <AuditLog />;                    // Part 6
    case 'Banking': return <Banking />;
    case 'Payments': return <Payments />;
    // case 'ProductRates': return <ProductRates />;
    case 'Contra': return <Contra />;
    case 'Journal': return <Journal />;
    case 'FinancialStatements': return <Financials />;
    case 'StockLedger': return <StockLedger />;
    case 'TankTransfer': return <TankTransfer />;
    case 'StockAdjustment': return <StockAdjustment />;
    case 'Employees': return <Employees />;
    case 'Overtime': return <Overtime />;
    case 'Reconciliation': return <Reconciliation />;
    case 'Ledgers': return <Ledgers />;
    case 'LedgerView': return <LedgerView />;
    case 'RoleManagement':
    case 'UserManagement': return <UserManagement subPage={page} />;
    case 'RegisterStation': return <RegisterStation />;
    case 'Approvals':       return <Approvals />;
    case 'Sitemap':         return <Sitemap />;
    case 'DailyEntry':      return <DailyEntry />;
    default:
      return (
        <div style={{ background: 'white', borderRadius: 6, padding: 48, textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
          <h2 style={{ color: 'var(--primary)', marginBottom: 8 }}>{page}</h2>
          <p style={{ color: 'var(--text-secondary)' }}>{t('common.comingSoon')}</p>
        </div>
      );
  }
}

function AppInner() {
  const { t } = useTranslation();
  const { user, loading, isTrialExpired } = useAuth();
  const [activePage, setActivePage] = useState(() => {
    const hash = window.location.hash.replace('#', '');
    return hash || 'Dashboard';
  });
  const [activeTab, setActiveTab] = useState('Meter Reading');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showManual, setShowManual] = useState(false);

  const handlePageChange = (page) => {
    if (isTrialExpired && page !== 'Dashboard' && page !== 'Support') return;
    setActivePage(page);
    setSidebarOpen(false);
  };

  useEffect(() => {
    if (!sidebarOpen) return;
    const close = (e) => {
      if (e.target.classList.contains('sidebar-overlay')) setSidebarOpen(false);
    };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [sidebarOpen]);

  // ── Sync Active Page with Hash ────────────────────────────
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '');
      if (hash && hash !== activePage) {
        setActivePage(hash);
      }
    };
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [activePage]);

  useEffect(() => {
    if (activePage) {
      window.location.hash = activePage;
    }
  }, [activePage]);

  // ── Global Search Navigation ───────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.goto) {
        setActivePage(e.detail.goto);
      }
    };
    window.addEventListener('pg:navigate', handler);
    return () => window.removeEventListener('pg:navigate', handler);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.page) setActivePage(e.detail.page);
    };
    window.addEventListener('navigate', handler);
    return () => window.removeEventListener('navigate', handler);
  }, []);

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: '#f0f4f8', fontFamily: 'Nunito,sans-serif', fontSize: 16, color: '#5a7286'
      }}>
        ⏳ {t('app.loadingApp')}
      </div>
    );
  }

  if (!user) return <AuthPage />;

  const handleTabChange = (tab) => {
    if (isTrialExpired && tab !== 'Dashboard' && tab !== 'Support') return;
    setActiveTab(tab);
    const mapping = TAB_TO_PAGE[tab];
    if (mapping) setActivePage(mapping.subPage || mapping.page);
    setSidebarOpen(false);
  };



  return (
    <div className="app-container">
      {sidebarOpen && (
        <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />
      )}

      <Sidebar
        activePage={activePage}
        setActivePage={handlePageChange}
        mobileOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      <div className="main-content">
        <TrialBanner />
        <Header
          onMenuToggle={() => setSidebarOpen(!sidebarOpen)}
          onOpenManual={() => setShowManual(true)}
        />
        <div className="page-content">
          {isTrialExpired && activePage !== 'Dashboard' && activePage !== 'Support' ? (
            <div style={{ textAlign: 'center', padding: '50px 20px', background: 'white', borderRadius: 8 }}>
              <h2 style={{ color: 'var(--danger)' }}>{t('app.trialExpired')}</h2>
              <p style={{ marginTop: 10, color: 'var(--text-secondary)' }}>
                {t('app.trialExpiredBody')}
              </p>
              <button className="btn btn-primary" style={{ marginTop: 20 }} onClick={() => setActivePage('Dashboard')}>
                {t('app.goToDashboard')}
              </button>
            </div>
          ) : (
            <PageContent page={activePage} />
          )}
        </div>
      </div>
      <ChatBot />

      {/* GLOBAL FULL-SCREEN MANUAL MODAL */}
      {showManual && (
        <div className="manual-modal-overlay" style={{
          position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
          background: 'rgba(0,0,0,0.95)', zIndex: 200000,
          display: 'flex', flexDirection: 'column'
        }}>
          <div className="manual-header" style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '15px 30px', background: '#2c3e50', color: 'white',
            boxShadow: '0 4px 15px rgba(0,0,0,0.5)'
          }}>
            <h3 style={{ margin: 0, fontSize: 20 }}>📘 {t('app.manualTitle')}</h3>
            <button
              className="btn"
              onClick={() => setShowManual(false)}
              style={{
                background: '#e74c3c', color: 'white', border: 'none',
                padding: '12px 25px', borderRadius: 6, fontWeight: 700,
                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10,
                fontSize: 15, boxShadow: '0 2px 5px rgba(0,0,0,0.2)'
              }}
            >
              ✕ {t('app.backToApp')}
            </button>
          </div>
          <div className="manual-body" style={{ flex: 1, background: '#525659' }}>
            <iframe
              src="/usermanual.pdf#view=FitH"
              width="100%"
              height="100%"
              style={{ border: 'none' }}
              title="User Manual"
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <ToastProvider>
        <AppInner />
      </ToastProvider>
    </AuthProvider>
  );
}
